import { useRef, useState, useEffect } from "react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import CampaignFolderIcon from "~/components/icons/CampaignFolderIcon";

interface PhotoDropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  error?: string;
  isBusy?: boolean;
}

const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);
// HEIC/HEIF stay in the accept attr so they're selectable; we convert them to
// JPEG client-side (see convertHeicToJpeg) instead of relying on iOS.
const ACCEPT_ATTR = "image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif";
const HEIC_TYPES = new Set(["image/heic", "image/heif"]);
const MAX_FILES = 4;
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const validateDimensions = (file: File): Promise<boolean> =>
  createImageBitmap(file).then(
    (bmp) => {
      const ok = bmp.width >= 400 && bmp.height >= 400;
      bmp.close();
      return ok;
    },
    () => false
  );

// Stable identity for a picked file. iOS reuses the same generic name (e.g.
// "image.jpg") for every camera capture, so name alone would wrongly treat
// distinct shots as duplicates and silently drop them. Size + lastModified
// disambiguate them.
const fileKey = (f: File) => `${f.name}-${f.lastModified}-${f.size}`;

// HEIC MIME is often missing (empty f.type) on desktop browsers, so also match
// the extension.
const isHeic = (f: File) =>
  HEIC_TYPES.has(f.type) || /\.(heic|heif)$/i.test(f.name);

// Convert a HEIC/HEIF file to JPEG in the browser. heic2any (libheif WASM) is
// lazy-imported so its weight only loads when a HEIC is actually picked.
const convertHeicToJpeg = async (file: File): Promise<File> => {
  const heic2any = (await import("heic2any")).default;
  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const name = `${file.name.replace(/\.(heic|heif)$/i, "")}.jpg`;
  // Preserve the source's lastModified so the composite fileKey is stable across
  // repeated conversions of the same photo (otherwise dedupe would miss them).
  return new File([blob], name, {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
};

interface PhotoThumbnailProps {
  file: File;
  onRemove: (e: React.MouseEvent, file: File) => void;
}

const PhotoThumbnail = ({ file, onRemove }: PhotoThumbnailProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <div className="photo-dropzone__thumb">
      {previewUrl && (
        <img
          src={previewUrl}
          alt={file.name}
          className="photo-dropzone__thumb-img"
        />
      )}
      <button
        type="button"
        onClick={(e) => onRemove(e, file)}
        aria-label={`Remove ${file.name}`}
        title={`Remove ${file.name}`}
        className="photo-dropzone__remove"
      >
        x
      </button>
    </div>
  );
};

const PhotoDropzone = ({ files, onChange, error, isBusy }: PhotoDropzoneProps) => {
  const { $pt } = usePrismicLabels();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileErrors, setFileErrors] = useState<string[]>([]);

  // applyFiles is async (HEIC conversion can take seconds), so the `files` prop
  // captured in its closure can be stale if the user picks again before it
  // finishes. Read the live list from this ref when merging to avoid dropping
  // files. (The isProcessing interaction guard below also prevents concurrent
  // runs during conversion — this is the belt to that suspenders.)
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    if (isBusy) setFileErrors([]);
  }, [isBusy]);

  const applyFiles = async (incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    const errors: string[] = [];
    const valid: File[] = [];

    // Only show the processing state when there's actually a HEIC to convert,
    // so normal images don't flash a spinner.
    const needsConversion = list.some(isHeic);
    if (needsConversion) setIsProcessing(true);

    try {
      for (const original of list) {
        let f = original;

        // Convert HEIC/HEIF to JPEG up front: browsers can't decode HEIC for the
        // dimension check, and the backend only accepts JPEG/PNG/GIF/WEBP.
        if (isHeic(f)) {
          try {
            f = await convertHeicToJpeg(f);
          } catch {
            errors.push($pt("campaign_photo_heic_error") ?? "");
            continue;
          }
        }

        if (!ACCEPTED_TYPES.has(f.type)) {
          errors.push($pt("campaign_photo_invalid_type_error") ?? "");
          continue;
        }
        if (f.size > MAX_SIZE_BYTES) {
          errors.push($pt("campaign_photo_max_size_error") ?? "");
          continue;
        }
        const dimensionsOk = await validateDimensions(f);
        if (!dimensionsOk) {
          errors.push($pt("campaign_photo_min_dimensions_error") ?? "");
          continue;
        }
        valid.push(f);
      }

      // Dedupe by composite key against both already-selected files and other
      // files in this same batch (a multi-select can contain same-named items).
      // Read from the ref, not the captured `files` prop, so a slow conversion
      // can't merge against a stale list and drop files.
      const currentFiles = filesRef.current;
      const seen = new Set(currentFiles.map(fileKey));
      const newFiles: File[] = [];
      for (const f of valid) {
        const key = fileKey(f);
        if (seen.has(key)) continue;
        seen.add(key);
        newFiles.push(f);
      }
      const merged = [...currentFiles, ...newFiles];
      if (merged.length > MAX_FILES) {
        errors.push($pt("campaign_photo_max_4_error") ?? "");
      }

      setFileErrors(errors.length > 0 ? [...new Set(errors)] : []);
      onChange(merged.slice(0, MAX_FILES));
    } finally {
      if (needsConversion) setIsProcessing(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) void applyFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) void applyFiles(e.dataTransfer.files);
  };

  const handleRemove = (e: React.MouseEvent, fileToRemove: File) => {
    e.stopPropagation();
    const updatedFiles = files.filter((file: File) => file !== fileToRemove);
    onChange(updatedFiles);
  };


  const isMaxReached = files.length >= MAX_FILES;
  // Disable interactions while converting so a second pick can't start a
  // concurrent applyFiles and race the first.
  const locked = isMaxReached || isProcessing;

  // Reset drag highlight if async validation completes while a drag is in progress
  // and the zone transitions to full/locked mid-drag (handlers become undefined).
  useEffect(() => {
    if (locked) setIsDragging(false);
  }, [locked]);

  return (
    <div className="photo-dropzone">
      <p className="campaign-field__label">
        {$pt("campaign_photo_upload_label")} <span className="photo-dropzone__required">*</span>
      </p>

      <div
        role={locked ? undefined : "button"}
        tabIndex={locked ? undefined : 0}
        onClick={locked ? undefined : () => inputRef.current?.click()}
        onKeyDown={
          locked
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }
        }
        onDragOver={locked ? undefined : handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        onDrop={locked ? undefined : handleDrop}
        className={`photo-dropzone__area${isDragging ? " photo-dropzone__area--dragging" : ""}${isMaxReached ? " photo-dropzone__area--full" : ""}`}
      >
        {!isMaxReached && (
          <>
            <CampaignFolderIcon className="photo-dropzone__icon" />
            <p className="photo-dropzone__text">
              <span className="link">{$pt("campaign_photo_click_here")}</span>{" "}
              {$pt("campaign_photo_drop_text")}
            </p>
            <p className="photo-dropzone__hint">{$pt("campaign_photo_hint")}</p>
          </>
        )}

        {files.length > 0 && (
          <div className="photo-dropzone__grid">
            {files.map((f: File) => (
              <PhotoThumbnail
                key={fileKey(f)}
                file={f}
                onRemove={handleRemove}
              />
            ))}
          </div>
        )}

        {isProcessing && (
          <p className="photo-dropzone__processing">
            {$pt("campaign_photo_converting") ?? "Bezig met converteren..."}
          </p>
        )}
      </div>

      {files.length > 0 && (
        <p className="photo-dropzone__counter">
          {files.length} / {MAX_FILES} {$pt("campaign_photo_counter_label")}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        name="photos"
        multiple
        accept={ACCEPT_ATTR}
        onChange={handleChange}
        className="photo-dropzone__input"
        aria-label={$pt("campaign_photo_upload_label") ?? undefined}
      />

      {fileErrors.map((msg) => (
        <span key={msg} className="photo-dropzone__error">{msg}</span>
      ))}
      {error && <span className="photo-dropzone__error">{error}</span>}
    </div>
  );
};

export default PhotoDropzone;
